/* eslint-disable no-underscore-dangle */
/* eslint-disable camelcase */
import {
  CONNECTION_STATE,
  DETECTOR_ACQUISITION_STATE,
  DETECTOR_EVENTS,
  DETECTOR_IMPLEMENTATION,
  DETECTOR_STATE,
} from 'app/types/xray';
import TimeoutPromise from 'app/utils/TimeoutPromise';
import EventEmitter from 'events';
import _ from 'lodash';
import { getAnatomicRegionFromString } from 'app/utils/xrayRegions';

export default class InMemoryFlatPanelDetector extends EventEmitter {
  /**
   *
   * @param {Object} detectorProps
   * @param {number} detectorProps.detectorID
   * @param {number} detectorProps.implementation
   * @param {string} detectorProps.serialNumber
   * @param {string} detectorProps.productModel
   * @param {[number, number]} detectorProps.pixelSpacing
   * @param {number} detectorProps.initialState
   * @param {number} detectorProps.initialAcquisitionState
   * @param {number} detectorProps.initialConnectionState
   * @param {Promise<Record<string, Record<string, DataImage[]>>>} detectorProps.imagesPromise
   */
  constructor({
    detectorID = 1,
    implementation = DETECTOR_IMPLEMENTATION.IRAY,
    serialNumber = 'abcefg',
    productModel = 'test_model',
    pixelSpacing = [50, 50],
    initialState = DETECTOR_STATE.Unknown,
    initialAcquisitionState = DETECTOR_ACQUISITION_STATE.Unknown,
    initialConnectionState = CONNECTION_STATE.Unknown,
    imagesPromise = undefined,
  } = {}) {
    super();

    this.detectorID = detectorID;
    this.implementation = implementation;
    this.serialNumber = serialNumber;
    this.productModel = productModel;
    this.pixelSpacing = pixelSpacing;
    this.state = initialState;
    this.acquisitionState = initialAcquisitionState;
    this.connectionState = initialConnectionState;

    this.images = {};
    imagesPromise?.then((images) => {
      this.images = images;
    });

    this.imagesIndex = 0;
    this.currentAnatomicRegion = 'dog entire_body';
  }

  getDetectorID = () => this.detectorID;

  getImplementation = () => this.implementation;

  getSerialNumber = () => this.serialNumber;

  getProductModel = () => this.productModel;

  getPixelSpacing = () => this.pixelSpacing;

  getState = () => this.state;

  getAcquisitionState = () => this.acquisitionState;

  getConnectionState = () => this.connectionState;

  connect = () => {
    this._updateState(DETECTOR_STATE.Ready);
    this._updateConnectionState(CONNECTION_STATE.OK);

    this.emit(DETECTOR_EVENTS.CONNECTED);
  };

  disconnect = () => {
    this._updateState(DETECTOR_STATE.Unknown);
    this._updateConnectionState(CONNECTION_STATE.Unknown);
    this.emit(DETECTOR_EVENTS.DISCONNECTED);
  };

  wakeUp = () => {
    if (this.state !== DETECTOR_STATE.Sleeping) return;
    this._updateState(DETECTOR_STATE.Ready);
  };

  sleep = () => {
    if (this.state === DETECTOR_STATE.Sleeping) return;
    this._updateState(DETECTOR_STATE.Sleeping);
  };

  startAutoAcq = () => {
    // Here we simulate a busy state chain with acquisition state change in the middle
    this._updateState(DETECTOR_STATE.Busy);
    this._updateAcquisitionState(DETECTOR_ACQUISITION_STATE.AutoTriggerPending);

    setTimeout(() => {
      this._updateAcquisitionState(DETECTOR_ACQUISITION_STATE.AutoTrigger);
      this._updateState(DETECTOR_STATE.Ready);
    }, 1000);
  };

  stopAutoAcq = () => {
    // Here we simulate a busy state chain with acquisition state change in the middle
    this._updateState(DETECTOR_STATE.Busy);

    setTimeout(() => {
      this._updateAcquisitionState(DETECTOR_ACQUISITION_STATE.Unknown);
      this._updateState(DETECTOR_STATE.Ready);
    }, 1000);
  };

  forceAcquire = () => {
    this._updateState(DETECTOR_STATE.Busy);

    (async () => {
      await TimeoutPromise(1000);
      this._updateAcquisitionState(DETECTOR_ACQUISITION_STATE.Manual);

      await this.acquireVirtualImage();

      this.startAutoAcq();
    })();
  };

  hasLastImageData = () => false;

  /* Demo/Test only methods */

  setCurrentAnatomicRegion = (anatomicRegion) => {
    this.currentAnatomicRegion = anatomicRegion;
  };

  acquireVirtualImage = async () => {
    this._updateState(DETECTOR_STATE.Busy);
    this.emit(DETECTOR_EVENTS.ACQUISITION_START);

    await TimeoutPromise(1000);

    this._emitNextImageInQueue();

    await TimeoutPromise(1000);

    this.emit(DETECTOR_EVENTS.ACQUISITION_END);

    this._updateState(DETECTOR_STATE.Ready);
  };

  retransmitVirtualImage = async () => {
    this._updateState(DETECTOR_STATE.Busy);

    await TimeoutPromise(1000);

    this._emitNextImageInQueue();

    await TimeoutPromise(500);

    this._updateState(DETECTOR_STATE.Ready);
  };

  /* Private methods */

  emit = async (...args) => {
    await undefined;
    super.emit(...args);
  };

  _updateState = (newState) => {
    const previousState = this.state;
    this.state = newState;

    this.emit(DETECTOR_EVENTS.STATUS_CHANGE, {
      previous_status: previousState,
      new_status: this.state,
    });
  };

  _updateConnectionState = (newState) => {
    const previousConnectionState = this.connectionState;
    this.connectionState = newState;

    this.emit(DETECTOR_EVENTS.CONNECTION_STATUS_CHANGE, {
      previous_status: previousConnectionState,
      new_status: this.connectionState,
    });
  };

  _updateAcquisitionState = (newState) => {
    const previousAcquisitionState = this.acquisitionState;
    this.acquisitionState = newState;

    this.emit(DETECTOR_EVENTS.ACQUISITION_STATUS_CHANGE, {
      previous_status: previousAcquisitionState,
      new_status: this.acquisitionState,
    });
  };

  _getImagesForCurrentAnatomicRegion = () => {
    const anatomicDescription = getAnatomicRegionFromString(this.currentAnatomicRegion);
    const { mainRegion, view } = anatomicDescription;
    let { specie } = anatomicDescription;
    if (!(specie in this.images)) {
      specie = 'dog';
    }
    let images;
    if (mainRegion in this.images[specie]) {
      images =
        this.images[specie]?.[mainRegion]?.[view]?.images ??
        this.images[specie]?.[mainRegion]?.images;
    } else {
      // For legs, we do not have the lateralization in the images keys
      // so we have to do a match with a startsWith
      const matchingRegionKey =
        Object.keys(this.images[specie] ?? {}).find((regionKey) =>
          regionKey.startsWith(mainRegion)
        ) ??
        Object.keys(this.images[specie] ?? {}).find((regionKey) =>
          regionKey.startsWith(mainRegion.split('_')[0])
        );
      if (matchingRegionKey) {
        images =
          this.images[specie]?.[matchingRegionKey]?.[view]?.images ??
          this.images[specie]?.[matchingRegionKey]?.images;
      }
    }

    return (
      images ??
      this.images[specie]?.entire_body ??
      this.images[specie]?.images ??
      this.images?.images
    );
  };

  _emitNextImageInQueue = () => {
    try {
      const imagesForRegion = this._getImagesForCurrentAnatomicRegion();
      if (imagesForRegion.length === 0) {
        const allImages = Object.values(this.images)
          .flatMap((specieImages) => Object.values(specieImages))
          .flat();
        if (allImages.length === 0) return;
        const imageToSend = allImages[this.imagesIndex % allImages.length];
        if (!imageToSend) return;
        this.imagesIndex += 1;
        const imageEvent = _.cloneDeep({
          data: imageToSend.data,
          width: imageToSend.width,
          height: imageToSend.height,
          bytes_per_pixel: imageToSend.bytes_per_pixel,
          maxPixelValue: imageToSend.maxPixelValue,
          minPixelValue: imageToSend.minPixelValue,
          windowCenter: imageToSend.windowCenter,
          windowWidth: imageToSend.windowWidth,
        });
        this.emit(DETECTOR_EVENTS.NEW_IMAGE, imageEvent);
        return;
      }

      const imageToSend = imagesForRegion[this.imagesIndex % imagesForRegion.length];

      if (!imageToSend) return;

      this.imagesIndex += 1;

      const imageEvent = _.cloneDeep({
        data: imageToSend.data,
        width: imageToSend.width,
        height: imageToSend.height,
        bytes_per_pixel: imageToSend.bytes_per_pixel,
        maxPixelValue: imageToSend.maxPixelValue,
        minPixelValue: imageToSend.minPixelValue,
        windowCenter: imageToSend.windowCenter,
        windowWidth: imageToSend.windowWidth,
      });

      this.emit(DETECTOR_EVENTS.NEW_IMAGE, imageEvent);
    } catch {
      //
    }
  };
}
